import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, cn, Item, ItemGroup, Text } from '@kognitos/lattice';
import { useResolvedFileDisplayName } from '@/shared/hooks';
import { formatDisplayName, truncateFilename } from '@/shared/utils';
import { getValueSummary } from '@/modules/outputs/components/ValueChip';
import { PreviewLayover } from '@/modules/outputs/components/PreviewLayover';
import { getResolvedFileDisplayName, isFile } from '@/modules/outputs/utils';
import { downloadOutput } from '@/modules/outputs/utils/download';
import { CHIP_MAX_LABEL_CHARS } from '@/modules/outputs/constants';
import type { TResolvedFile, TResolvedValue } from '@/modules/outputs/types';

/**
 * Props for the ValueList component.
 */
export interface IValueListProps {
  /** Name/value pairs, in display order. `undefined` means not known yet. */
  entries: [string, TResolvedValue | undefined][];
  /** Accessible name for the list, e.g. "Step inputs" */
  ariaLabel?: string;
  /** Shown in place of a value that isn't known, e.g. a masked secret. */
  placeholders?: Partial<Record<string, string>>;
  /** A short tag after a row's value, e.g. where it comes from. */
  tags?: Partial<Record<string, string>>;
  /** Rows to show before a "Show N more" control. */
  maxRows?: number;
  /**
   * Rows open their value in the preview layover. Off for plain text, where
   * a clipped name or value shows in full in a tooltip instead.
   */
  interactive?: boolean;
  /**
   * Shrink the box to its content, sizing the name column to the longest name,
   * for wide hosts (the runs table) where a 40% name column pushes values far
   * from their names.
   */
  fitContent?: boolean;
  /** Leave out each value's name, for a host that already names it, e.g. a table column. */
  showNames?: boolean;
  /** Extra classes for the box, e.g. a stronger fill on a busier surface. */
  className?: string;
}

const ROW_CLASS =
  'min-h-6 w-full flex-nowrap gap-2 rounded-none px-2.5 py-1 text-left';
// Each row joins the list's grid so names line up across rows.
const FIT_ROW_CLASS = 'col-span-full grid grid-cols-subgrid gap-x-4';

/**
 * A file's real filename, middle-truncated so siblings sharing a prefix stay
 * distinguishable.
 *
 * @param props - The file value
 * @returns {JSX.Element} The filename
 */
function FileSummary({
  value,
  full = false,
}: {
  value: TResolvedFile;
  full?: boolean;
}) {
  const { t } = useTranslation('outputs');
  const fileUrl = value.type === 'file-remote' ? value.fileUrl : undefined;
  const fallback = getResolvedFileDisplayName(value) ?? t('outputs.chip.file');
  const { displayName } = useResolvedFileDisplayName(fileUrl, fallback);
  return (
    <>
      {full ? displayName : truncateFilename(displayName, CHIP_MAX_LABEL_CHARS)}
    </>
  );
}

/**
 * Named values as one-line rows in a muted box: the name, then a short preview
 * of the value (text, count, a record's name, a filename). Each row opens the
 * value in the preview layover, with the other entries as its navigation,
 * unless `interactive` is off.
 *
 * A row with no value yet shows its name and optional placeholder, and isn't
 * clickable.
 *
 * @param {IValueListProps} props - Component props
 * @returns {JSX.Element | null} The list, or null with no entries
 */
export const ValueList = ({
  entries,
  ariaLabel,
  placeholders,
  tags,
  maxRows,
  interactive = true,
  fitContent = false,
  showNames = true,
  className,
}: IValueListProps) => {
  const { t } = useTranslation('outputs');
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  if (entries.length === 0) return null;

  const visible = maxRows && !showAll ? entries.slice(0, maxRows) : entries;
  const hiddenCount = entries.length - visible.length;
  // Clickable rows open the rest in a preview; plain ones clip with a tooltip,
  // which only shows when the text actually overflows.
  const fit = interactive ? 'truncate' : undefined;
  const ellipsis = interactive ? undefined : { tooltip: true };
  const rowClass = cn(ROW_CLASS, fitContent && FIT_ROW_CLASS);
  const nameWidth = fitContent ? 'shrink-0' : 'w-2/5 shrink-0';

  const valued = entries.filter(
    (entry): entry is [string, TResolvedValue] => entry[1] !== undefined,
  );
  const opened = valued.find(([key]) => key === openKey);

  return (
    <>
      <ItemGroup
        role="group"
        aria-label={ariaLabel}
        className={cn(
          'overflow-hidden rounded-lg bg-muted/50',
          !fitContent && 'w-full',
          fitContent && 'grid w-fit max-w-full',
          // A tag takes a third column, so only reserve one when there are tags.
          fitContent &&
            (!showNames
              ? 'grid-cols-[minmax(0,1fr)]'
              : tags
                ? 'grid-cols-[auto_minmax(0,1fr)_auto]'
                : 'grid-cols-[auto_minmax(0,1fr)]'),
          className,
        )}
        data-testid="value-list"
      >
        {visible.map(([key, value]) => {
          const name = formatDisplayName(key);
          const tag = tags?.[key];

          if (value === undefined) {
            const placeholder = placeholders?.[key];
            return (
              <Item
                key={key}
                size="sm"
                className={rowClass}
                data-testid={`value-list-pending-${key}`}
              >
                <Text
                  level="xSmall"
                  color="muted"
                  ellipsis={ellipsis}
                  className={cn(fit, placeholder ? nameWidth : 'min-w-0')}
                >
                  {name}
                </Text>
                {placeholder && (
                  <Text
                    level="xSmall"
                    color="muted"
                    ellipsis={ellipsis}
                    className={cn('min-w-0 flex-1', fit)}
                  >
                    {placeholder}
                  </Text>
                )}
              </Item>
            );
          }

          const row = (
            <>
              {showNames && (
                <Text
                  level="xSmall"
                  color="muted"
                  ellipsis={ellipsis}
                  className={cn(nameWidth, fit)}
                >
                  {name}
                </Text>
              )}
              <Text
                level="xSmall"
                ellipsis={ellipsis}
                className={cn('min-w-0 flex-1', fit)}
              >
                {isFile(value) ? (
                  <FileSummary
                    value={value as TResolvedFile}
                    full={!interactive}
                  />
                ) : !interactive && typeof value === 'string' ? (
                  value
                ) : (
                  getValueSummary(value, t, { canPeek: true })
                )}
              </Text>
              {tag && <Badge variant="outline">{tag}</Badge>}
            </>
          );

          if (!interactive) {
            return (
              <Item key={key} size="sm" className={rowClass}>
                {row}
              </Item>
            );
          }

          return (
            <Item
              key={key}
              asChild
              size="sm"
              className={cn(
                rowClass,
                'cursor-pointer hover:bg-muted focus-visible:ring-2 focus-visible:ring-inset',
              )}
            >
              <button
                type="button"
                aria-label={t('outputs.card.openPreview', { name })}
                onClick={() => setOpenKey(key)}
              >
                {row}
              </button>
            </Item>
          );
        })}
      </ItemGroup>
      {maxRows !== undefined && entries.length > maxRows && (
        <Button
          type="button"
          variant="link"
          size="xs"
          className="self-start px-0"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll
            ? t('outputs.valueList.showLess')
            : t('outputs.valueList.showMore', { count: hiddenCount })}
        </Button>
      )}

      {opened && (
        <PreviewLayover
          key={opened[0]}
          isOpen
          outputName={opened[0]}
          outputValue={opened[1]}
          onClose={() => setOpenKey(null)}
          onDownload={downloadOutput}
          navigationItems={valued.map(([key, value]) => ({
            name: key,
            value,
            displayName: formatDisplayName(key),
          }))}
        />
      )}
    </>
  );
};
